import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFamilyCompletionData } from '../utils/familyCompletion';
import { BIBLE_BOOKS } from '../utils/bibleData';

function ChapterList({ chapters }) {
  const [expanded, setExpanded] = useState(false);
  const displayChapters = expanded ? chapters : chapters.slice(0, 12);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {displayChapters.map((ch, i) => {
          const book = ch.book ? (ch.book.charAt(0).toUpperCase() + ch.book.slice(1)) : '';
          return (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-cerulean/8 text-cerulean text-xs font-medium border border-cerulean/15">
              {book} {ch.chapter}
            </span>
          );
        })}
        {!expanded && chapters.length > 12 && (
          <button
            onClick={() => setExpanded(true)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs font-medium hover:bg-gray-200 transition-colors"
          >
            +{chapters.length - 12} more
          </button>
        )}
        {expanded && chapters.length > 12 && (
          <button
            onClick={() => setExpanded(false)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs font-medium hover:bg-gray-200 transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export default function PlansPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [userRecordingPlans, setUserRecordingPlans] = useState([]);
  const [familyCompletedKeys, setFamilyCompletedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    async function load() {
      if (!currentUser) return;
      try {
        const [plansSnap, userPlansSnap, userDoc] = await Promise.all([
          getDocs(collection(db, 'plans')),
          getDocs(collection(db, `users/${currentUser.uid}/recordingPlans`)),
          getDoc(doc(db, 'users', currentUser.uid)),
        ]);
        setPlans(plansSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setUserRecordingPlans(userPlansSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Load family completion data to detect completed plans
        const familyIds = userDoc.data()?.familyIds || [];
        if (familyIds.length > 0) {
          const completionData = await getFamilyCompletionData(familyIds[0], db);
          if (completionData?.chapters) {
            const keys = new Set(
              completionData.chapters
                .filter(ch => ch.completed)
                .map(ch => `${ch.book}_${ch.chapter}`)
            );
            setFamilyCompletedKeys(keys);
          }
        }
      } catch (err) {
        console.error('Error loading plans:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser]);

  const activePlanId = userRecordingPlans.find(p => p.status === 'active')?.planId;

  const handleSubscribe = async (plan) => {
    if (!currentUser || subscribing) return;
    setSubscribing(plan.id);
    try {
      // Mark any existing active plan as inactive (don't delete — preserves progress history)
      const existingActive = userRecordingPlans.find(rp => rp.status === 'active');
      if (existingActive) {
        await updateDoc(doc(db, `users/${currentUser.uid}/recordingPlans`, existingActive.id), {
          status: 'inactive',
        });
      }

      // Check if there's already a subscription doc for this plan (previously subscribed)
      const existingForPlan = userRecordingPlans.find(rp => rp.planId === plan.id);
      if (existingForPlan) {
        await updateDoc(doc(db, `users/${currentUser.uid}/recordingPlans`, existingForPlan.id), {
          status: 'active',
          reactivatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, `users/${currentUser.uid}/recordingPlans`), {
          planId: plan.id,
          planName: plan.name,
          subscribedAt: serverTimestamp(),
          completedChapters: [],
          currentChapterIndex: 0,
          totalChapters: plan.chapters?.length || 0,
          lastRecordedAt: null,
          status: 'active',
          chapterProgress: {},
        });
      }

      const snap = await getDocs(collection(db, `users/${currentUser.uid}/recordingPlans`));
      setUserRecordingPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      navigate('/record');
    } catch (err) {
      console.error('Error subscribing to plan:', err);
    } finally {
      setSubscribing(null);
    }
  };

  const handleUnsubscribe = async (plan) => {
    if (!currentUser || subscribing) return;
    setSubscribing(plan.id);
    try {
      const existing = userRecordingPlans.find(rp => rp.planId === plan.id);
      if (existing) {
        await updateDoc(doc(db, `users/${currentUser.uid}/recordingPlans`, existing.id), {
          status: 'inactive',
        });
        const snap = await getDocs(collection(db, `users/${currentUser.uid}/recordingPlans`));
        setUserRecordingPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error('Error unsubscribing:', err);
    } finally {
      setSubscribing(null);
    }
  };

  return (
    <div className="bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 min-h-screen pb-24">
      <div className="max-w-2xl mx-auto px-4 pt-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Chapter Plans</h1>
            <p className="text-white/60 text-xs mt-0.5">Choose a plan to guide your family recordings</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
          </div>
        ) : plans.length === 0 ? (
          <div className="bg-white/10 rounded-2xl p-8 text-center text-white/60">
            No plans available yet.
          </div>
        ) : (
          <div className="space-y-4">
            {plans.map(plan => {
              const isActive = plan.id === activePlanId;
              const isLoading = subscribing === plan.id;
              const chapters = plan.chapters || [];
              const userPlan = userRecordingPlans.find(rp => rp.planId === plan.id);

              // Progress from subscription doc
              const planChapterKeys = new Set(chapters.map(ch => `${ch.book}_${ch.chapter}`));
              const completedKeys = new Set([
                ...(userPlan?.completedChapters || []).filter(k => planChapterKeys.has(k)),
                ...Object.entries(userPlan?.chapterProgress || {})
                  .filter(([k, v]) => v.isComplete && planChapterKeys.has(k))
                  .map(([k]) => k),
              ]);
              const done = completedKeys.size;
              const total = chapters.length;
              const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

              // Completed = all plan chapters recorded by family (works even without subscription doc)
              const isPlanComplete = total > 0 && chapters.every(ch =>
                familyCompletedKeys.has(`${ch.book}_${ch.chapter}`)
              );

              return (
                <div
                  key={plan.id}
                  className={`rounded-2xl shadow-lg overflow-hidden transition-all ${
                    isActive
                      ? 'bg-gray-100 ring-2 ring-cerulean'
                      : isPlanComplete
                      ? 'bg-white ring-2 ring-aqua-deep/40'
                      : 'bg-white'
                  }`}
                >
                  {/* Plan header */}
                  <div className="p-5 pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-lg font-bold text-space-cadet">{plan.name}</h2>
                          {isPlanComplete && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gradient-to-r from-orange-400 to-violet-500 text-white text-xs font-bold rounded-full shadow-sm">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                              Completed!
                            </span>
                          )}
                          {isActive && !isPlanComplete && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cerulean/10 text-cerulean text-xs font-semibold rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              Active
                            </span>
                          )}
                        </div>
                        {plan.description && (
                          <p className="text-gray-500 text-sm mt-1 leading-relaxed">{plan.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="text-2xl font-bold text-cerulean">{chapters.length}</span>
                        <p className="text-xs text-gray-400 leading-none">chapters</p>
                      </div>
                    </div>

                    {/* Progress bar — shown when subscribed and not yet complete */}
                    {userPlan && total > 0 && !isPlanComplete && (
                      <div className="mt-3 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">{done} of {total} chapters recorded</span>
                          <span className="font-semibold text-cerulean">{pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cerulean to-aqua-deep transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Chapters */}
                    {chapters.length > 0 && <ChapterList chapters={chapters} />}
                  </div>

                  {/* Footer actions */}
                  <div className={`px-5 pb-5 pt-3 border-t flex gap-3 ${isActive ? 'border-gray-200' : 'border-gray-50'}`}>
                    {isActive ? (
                      <>
                        <button
                          onClick={() => navigate('/record')}
                          className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all shadow-sm text-sm"
                        >
                          Continue Recording
                        </button>
                        <button
                          onClick={() => handleUnsubscribe(plan)}
                          disabled={isLoading}
                          className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors font-medium"
                        >
                          {isLoading ? 'Unsubscribing…' : 'Unsubscribe'}
                        </button>
                      </>
                    ) : isPlanComplete ? (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isLoading}
                        className="flex-1 bg-gray-200 text-gray-600 font-semibold py-2.5 rounded-xl hover:bg-gray-300 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" /> Working…</>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            Re-Record
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all shadow-sm disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Subscribing…</>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Subscribe & Start Recording
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Chapter Search */}
        <ChapterSearch navigate={navigate} />
      </div>
    </div>
  );
}

const BOOK_NAME_MAP = Object.fromEntries(BIBLE_BOOKS.map(b => [b.id, b.name]));

function ChapterSearch({ navigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [summaries, setSummaries] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const ensureSummaries = async () => {
    if (summaries) return summaries;
    setLoading(true);
    const res = await fetch('/chapter-summaries.json');
    const data = await res.json();
    setSummaries(data);
    setLoading(false);
    return data;
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 3) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      const data = await ensureSummaries();
      const q = val.trim().toLowerCase();
      const hits = [];
      for (const [book, chapters] of Object.entries(data)) {
        for (const [ch, summary] of Object.entries(chapters)) {
          if (summary.toLowerCase().includes(q)) {
            hits.push({ book, chapter: parseInt(ch, 10), summary });
            if (hits.length >= 20) break;
          }
        }
        if (hits.length >= 20) break;
      }
      setResults(hits);
    }, 250);
  };

  const recordChapter = (book, chapter) => {
    localStorage.setItem('fb_recordPoint', JSON.stringify({ book, chapter, startVerse: 1, endVerse: 1 }));
    navigate('/record');
  };

  return (
    <div className="mt-8">
      <h2 className="text-white font-bold text-lg mb-1">Search Chapters by Topic</h2>
      <p className="text-white/60 text-sm mb-3">Find a chapter to record by searching the study summaries</p>

      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="e.g. creation, faith, covenant, Holy Spirit…"
          className="w-full pl-9 pr-4 py-3 rounded-xl bg-white text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-cerulean text-sm"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-cerulean" />
        )}
      </div>

      {query.trim().length > 0 && query.trim().length < 3 && (
        <p className="text-white/50 text-xs text-center py-2">Type at least 3 characters to search</p>
      )}

      {results.length > 0 && (
        <div className="space-y-3">
          {results.map(({ book, chapter, summary }) => (
            <div key={`${book}_${chapter}`} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-space-cadet text-sm">
                    {BOOK_NAME_MAP[book] || book} {chapter}
                  </p>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed line-clamp-3">{summary}</p>
                </div>
                <button
                  onClick={() => recordChapter(book, chapter)}
                  className="flex-shrink-0 bg-gradient-to-r from-cerulean to-aqua-deep text-white text-xs font-semibold px-3 py-2 rounded-lg hover:opacity-90 transition-all whitespace-nowrap"
                >
                  Record this
                </button>
              </div>
            </div>
          ))}
          {results.length === 20 && (
            <p className="text-white/50 text-xs text-center">Showing first 20 results — refine your search for more</p>
          )}
        </div>
      )}

      {query.trim().length >= 3 && !loading && results.length === 0 && (
        <p className="text-white/50 text-sm text-center py-4">No chapters found for "{query}"</p>
      )}
    </div>
  );
}
